(() => {
  const componentName = 'product-description-tabs';
  const mobileAccordionQuery = window.matchMedia('(max-width: 749px)');

  function nodeHasContent(node) {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent.trim().length > 0;
    if (node.nodeType !== Node.ELEMENT_NODE) return false;
    if (node.textContent.trim().length > 0) return true;

    return Boolean(
      node.matches('img, video, iframe, table, ul, ol, svg') ||
        node.querySelector('img, video, iframe, table, a[href], ul, ol, svg')
    );
  }

  function getContentRoot(source) {
    const directHeadings = Array.from(source.children).filter((element) => element.matches('h5'));
    if (directHeadings.length > 0) return source;

    const meaningfulChildren = Array.from(source.childNodes).filter(nodeHasContent);
    if (meaningfulChildren.length !== 1 || meaningfulChildren[0].nodeType !== Node.ELEMENT_NODE) return source;

    const wrapper = meaningfulChildren[0];
    return Array.from(wrapper.children).some((element) => element.matches('h5')) ? wrapper : source;
  }

  function splitDescription(source, firstTitle) {
    const contentRoot = getContentRoot(source);
    const nodes = Array.from(contentRoot.childNodes);
    const sections = [{ title: firstTitle, nodes: [] }];
    let currentSection = sections[0];
    let foundHeading = false;

    nodes.forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE && node.matches('h5')) {
        const title = node.textContent.trim();

        if (title) {
          currentSection = { title, nodes: [] };
          sections.push(currentSection);
          foundHeading = true;
        }
        return;
      }

      currentSection.nodes.push(node);
    });

    if (!foundHeading) {
      return [{ title: firstTitle, nodes: Array.from(source.childNodes) }];
    }

    source.replaceChildren();
    return sections.filter((section) => section.title && section.nodes.some(nodeHasContent));
  }

  function appendNodes(container, nodes) {
    nodes.forEach((node) => container.appendChild(node));
  }

  function activateSection(component, tabs, panels, activeIndex, options = {}) {
    const isAccordion = component.dataset.layout === 'accordion';

    component.activeSectionIndex = activeIndex;

    tabs.forEach((tab, index) => {
      const isActive = index === activeIndex;

      if (isAccordion) {
        tab.removeAttribute('aria-selected');
        tab.setAttribute('aria-expanded', isActive ? 'true' : 'false');
        tab.tabIndex = 0;
        panels[index].setAttribute('role', 'region');
      } else {
        tab.removeAttribute('aria-expanded');
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.tabIndex = isActive ? 0 : -1;
        panels[index].setAttribute('role', 'tabpanel');
      }

      panels[index].hidden = !isActive;
    });

    const activeTab = tabs[activeIndex];
    if (!activeTab) return;

    if (options.moveFocus) activeTab.focus({ preventScroll: true });

    if (options.scroll) {
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      activeTab.scrollIntoView({
        behavior: reduceMotion ? 'auto' : 'smooth',
        block: isAccordion ? 'start' : 'nearest',
        inline: isAccordion ? 'nearest' : 'center',
      });
    }
  }

  function applyLayout(component, tabs, panels, tablist) {
    const isAccordion = mobileAccordionQuery.matches;
    let activeIndex = Number.isInteger(component.activeSectionIndex) ? component.activeSectionIndex : 0;

    component.dataset.layout = isAccordion ? 'accordion' : 'tabs';

    if (isAccordion) {
      tablist.setAttribute('role', 'group');
    } else {
      tablist.setAttribute('role', 'tablist');
      if (activeIndex < 0) activeIndex = 0;
    }

    activateSection(component, tabs, panels, activeIndex);
  }

  function buildTabs(component, source, interfaceElement, tablist, panelsContainer, firstTitle) {
    const sections = splitDescription(source, firstTitle);
    if (sections.length === 0) {
      component.remove();
      return;
    }

    const tabs = [];
    const panels = [];

    sections.forEach((section, index) => {
      const tabId = `${component.id}-tab-${index + 1}`;
      const panelId = `${component.id}-panel-${index + 1}`;
      const tab = document.createElement('button');
      const panel = document.createElement('div');

      tab.type = 'button';
      tab.id = tabId;
      tab.className = 'product-description-tabs__tab';
      tab.role = 'tab';
      tab.textContent = section.title;
      tab.setAttribute('aria-controls', panelId);
      tab.style.setProperty('--description-mobile-order', String(index * 2 + 1));

      panel.id = panelId;
      panel.className = 'product-description-tabs__panel rte text-subtext';
      panel.role = 'tabpanel';
      panel.setAttribute('aria-labelledby', tabId);
      panel.tabIndex = 0;
      panel.style.setProperty('--description-mobile-order', String(index * 2 + 2));
      appendNodes(panel, section.nodes);

      tab.addEventListener('click', () => {
        const isAccordion = component.dataset.layout === 'accordion';
        const nextIndex = isAccordion && component.activeSectionIndex === index ? -1 : index;

        activateSection(component, tabs, panels, nextIndex, {
          scroll: nextIndex >= 0,
        });
      });
      tab.addEventListener('keydown', (event) => {
        if (component.dataset.layout === 'accordion') return;

        let nextIndex = index;

        if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
        if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = tabs.length - 1;
        if (nextIndex === index && !['Home', 'End'].includes(event.key)) return;

        event.preventDefault();
        activateSection(component, tabs, panels, nextIndex, {
          moveFocus: true,
          scroll: true,
        });
      });

      tabs.push(tab);
      panels.push(panel);
      tablist.appendChild(tab);
      panelsContainer.appendChild(panel);
    });

    source.hidden = true;
    interfaceElement.hidden = false;
    component.activeSectionIndex = 0;
    component.layoutChangeHandler = () => applyLayout(component, tabs, panels, tablist);
    if (mobileAccordionQuery.addEventListener) {
      mobileAccordionQuery.addEventListener('change', component.layoutChangeHandler);
    } else {
      mobileAccordionQuery.addListener(component.layoutChangeHandler);
    }
    applyLayout(component, tabs, panels, tablist);
    component.dataset.ready = 'true';
  }

  class ProductDescriptionTabs extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;

      const source = this.querySelector('[data-description-source]');
      const interfaceElement = this.querySelector('[data-description-interface]');
      const tablist = this.querySelector('[data-description-tablist]');
      const panelsContainer = this.querySelector('[data-description-panels]');

      if (!source || !interfaceElement || !tablist || !panelsContainer) return;

      buildTabs(
        this,
        source,
        interfaceElement,
        tablist,
        panelsContainer,
        this.dataset.firstTitle || 'Beschreibung'
      );
    }
  }

  function initializeLegacyMobileDescription(root, index) {
    if (root.dataset.autoDescriptionReady === 'true') return;

    const originalDetails = root.querySelector(':scope > details');
    const originalSource = originalDetails?.querySelector('.bt-mobile-description-content');
    if (!originalDetails || !originalSource) return;

    const originalSummary = originalDetails.querySelector('summary');
    const firstTitle = originalSummary?.textContent.trim() || 'Beschreibung';
    const component = document.createElement(componentName);
    const source = document.createElement('div');
    const interfaceElement = document.createElement('div');
    const tablist = document.createElement('div');
    const panelsContainer = document.createElement('div');

    component.id = `ProductDescriptionTabs-mobile-${index + 1}`;
    component.className = 'product-description-tabs';
    component.dataset.firstTitle = firstTitle;

    source.className = 'product-description-tabs__source rte text-subtext';
    source.dataset.descriptionSource = '';
    appendNodes(source, Array.from(originalSource.childNodes));

    interfaceElement.className = 'product-description-tabs__interface';
    interfaceElement.dataset.descriptionInterface = '';
    interfaceElement.hidden = true;

    tablist.className = 'product-description-tabs__tablist';
    tablist.dataset.descriptionTablist = '';
    tablist.role = 'tablist';
    tablist.setAttribute('aria-label', 'Produktinformationen');

    panelsContainer.className = 'product-description-tabs__panels';
    panelsContainer.dataset.descriptionPanels = '';

    interfaceElement.append(tablist, panelsContainer);
    component.append(source, interfaceElement);
    root.replaceChildren(component);
    root.dataset.autoDescriptionReady = 'true';
  }

  function initializeLegacyMobileDescriptions(scope = document) {
    scope.querySelectorAll('.bt-mobile-description-only').forEach(initializeLegacyMobileDescription);
  }

  if (!customElements.get(componentName)) {
    customElements.define(componentName, ProductDescriptionTabs);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeLegacyMobileDescriptions(), { once: true });
  } else {
    initializeLegacyMobileDescriptions();
  }

  document.addEventListener('shopify:section:load', (event) => initializeLegacyMobileDescriptions(event.target));
})();
